import React, { useState, useRef, useEffect } from 'react';
import {
  Container,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Box,
  Snackbar,
  Alert,
  Checkbox
} from '@mui/material';
import {
  Mic as MicIcon,
  Stop as StopIcon,
  Save as SaveIcon,
  SmartToy as AIIcon,
  Edit as EditIcon,
  AutoFixHigh as AutoFixHighIcon,
  ContentCopy,
  Upload as UploadIcon,
  FormatAlignLeft as FormatAlignLeftIcon,
  KeyboardVoice as KeyboardVoiceIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

// Definições de tipo para Web Speech API
interface SpeechRecognitionResult {
  readonly isFinal: boolean;
  readonly [index: number]: SpeechRecognitionAlternative;
  length: number;
}

interface SpeechRecognitionAlternative {
  readonly transcript: string;
  readonly confidence: number;
}

interface SpeechRecognitionResultList {
  readonly [index: number]: SpeechRecognitionResult;
  length: number;
}

interface SpeechRecognitionEvent extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  readonly error: string;
  readonly message: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  onresult: (event: SpeechRecognitionEvent) => void;
  onerror: (event: SpeechRecognitionErrorEvent) => void;
  onend: () => void;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognition;
}

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

interface TranscriptionData {
  id?: string;
  transcriptionText: string;
  transcricaoIa: string;
  patientName: string;
  type: string;
  notes: string;
  status: string;
  audioBlob?: Blob;
  audioFilename?: string;
}

const NewTranscription: React.FC = () => {
  const navigate = useNavigate();
  const [transcriptionData, setTranscriptionData] = useState<TranscriptionData>({
    transcriptionText: '',
    transcricaoIa: '',
    patientName: '',
    type: 'consulta',
    notes: '',
    status: 'pending'
  });

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState<string | null>(null);
  const [isRealTime, setIsRealTime] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info'>('success');
  const [isFormatting, setIsFormatting] = useState(false);
  const [saveAudioFile, setSaveAudioFile] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      stopRecording();
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, []);

  const startRealTimeTranscription = () => {
    if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
      const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognitionAPI) {
        setSnackbarMessage('Seu navegador não suporta transcrição em tempo real');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      recognitionRef.current = new SpeechRecognitionAPI();
      
      recognitionRef.current.lang = 'pt-BR';
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;

      recognitionRef.current.onresult = (event: SpeechRecognitionEvent) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          const transcript = result[0].transcript;
          
          if (result.isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }

        if (finalTranscript) {
          console.log('[Transcrição] Texto final recebido:', { finalTranscript });
          setTranscriptionData(prev => ({
            ...prev,
            transcriptionText: prev.transcriptionText + finalTranscript
          }));
        }
      };

      recognitionRef.current.onend = () => {
        console.log('[Transcrição] Reconhecimento finalizado, reiniciando...');
        // Só reinicia se ainda estiver gravando
        if (mediaRecorderRef.current?.state === 'recording') {
          try {
            recognitionRef.current?.start();
            console.log('[Transcrição] Reconhecimento reiniciado com sucesso');
          } catch (error) {
            console.error('[Transcrição] Erro ao reiniciar reconhecimento:', error);
            setSnackbarMessage('Erro ao reiniciar a transcrição');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
          }
        }
      };

      recognitionRef.current.onerror = (event: SpeechRecognitionErrorEvent) => {
        console.error('Erro na transcrição em tempo real:', event.error);
        setSnackbarMessage(`Erro na transcrição em tempo real: ${event.error}`);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      };

      try {
        recognitionRef.current.start();
        console.log('[Transcrição] Reconhecimento de fala iniciado');
      } catch (error) {
        console.error('[Transcrição] Erro ao iniciar reconhecimento:', error);
      }
    } else {
      setSnackbarMessage('Seu navegador não suporta transcrição em tempo real');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const stopRealTimeTranscription = () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
        console.log('[Transcrição] Reconhecimento de fala parado');
      } catch (error) {
        console.error('[Transcrição] Erro ao parar reconhecimento:', error);
      }
    }
  };

  const startRecording = async () => {
    try {
      console.log('[Gravação] Iniciando solicitação de permissão do microfone');
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      console.log('[Gravação] Permissão concedida, configurando stream');
      streamRef.current = stream;
      
      // Sempre iniciar a gravação do áudio, mesmo em modo tempo real
      console.log('[Gravação] Iniciando gravação de áudio');
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm'
      });
      
      console.log('[Gravação] MediaRecorder criado com configurações:', {
        mimeType: mediaRecorder.mimeType,
        state: mediaRecorder.state
      });
      
      mediaRecorderRef.current = mediaRecorder;
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          console.log('[Gravação] Chunk de áudio recebido:', {
            size: event.data.size,
            type: event.data.type,
            timestamp: new Date().toISOString()
          });
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        console.log('[Gravação] Finalizando gravação');
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const timestamp = new Date().getTime();
        const filename = `audio_${timestamp}.webm`;
        
        console.log('[Gravação] Áudio finalizado:', {
          type: audioBlob.type,
          size: audioBlob.size,
          chunks: chunksRef.current.length,
          filename
        });
        
        const url = URL.createObjectURL(audioBlob);
        setAudioPreviewUrl(url);
        setTranscriptionData(prev => ({ 
          ...prev, 
          audioBlob: audioBlob,
          audioFilename: filename
        }));
        chunksRef.current = [];
      };

      mediaRecorder.start(100);
      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);
      
      if (isRealTime) {
        console.log('[Gravação] Iniciando transcrição em tempo real');
        startRealTimeTranscription();
      }
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error: any) {
      console.error('[Gravação] Erro ao iniciar gravação:', error);
      setSnackbarMessage(`Erro ao iniciar gravação: ${error?.message || 'Erro desconhecido'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const pauseRecording = () => {
    console.log('[Gravação] Pausando gravação e transcrição');
    if (mediaRecorderRef.current && isRecording) {
      if (!isPaused) {
        // Pausando
        mediaRecorderRef.current.pause();
        if (recognitionRef.current && isRealTime) {
          stopRealTimeTranscription();
        }
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      } else {
        // Retomando
        mediaRecorderRef.current.resume();
        if (isRealTime) {
          startRealTimeTranscription();
        }
        timerRef.current = setInterval(() => {
          setRecordingTime(prev => prev + 1);
        }, 1000);
      }
      setIsPaused(!isPaused);
    }
  };

  const stopRecording = () => {
    console.log('[Gravação] Iniciando parada da gravação', {
      isRealTime,
      isRecording,
      isPaused,
      mediaRecorderState: mediaRecorderRef.current?.state
    });

    if (isRealTime) {
      stopRealTimeTranscription();
    }

    if (mediaRecorderRef.current && isRecording) {
      try {
        mediaRecorderRef.current.stop();
        console.log('[Gravação] MediaRecorder parado');

        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => {
            track.stop();
            console.log('[Gravação] Track de áudio parado:', {
              kind: track.kind,
              id: track.id
            });
          });
        }

        if (timerRef.current) {
          clearInterval(timerRef.current);
          console.log('[Gravação] Timer limpo');
        }
        
        setIsRecording(false);
        setIsPaused(false);
      } catch (error) {
        console.error('[Gravação] Erro ao parar gravação:', error);
      }
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setAudioUrl(url);
      setTranscriptionData(prev => ({ ...prev, audioBlob: file }));
    }
  };

  const handleTranscribeAudio = async () => {
    if (!transcriptionData.audioBlob) {
      setSnackbarMessage('Nenhum áudio gravado ou selecionado');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    const formData = new FormData();
    formData.append('audio', transcriptionData.audioBlob);
    formData.append('type', transcriptionData.type);
    formData.append('patientName', transcriptionData.patientName);
    formData.append('notes', transcriptionData.notes);

    try {
      setIsRecording(false);
      const response = await api.post('/transcriptions/transcribe', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000,
      });
      
      setTranscriptionData(prev => ({
        ...prev,
        transcriptionText: response.data.transcription,
        transcricaoIa: response.data.transcription
      }));

      setSnackbarMessage('Áudio transcrito com sucesso!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Erro ao transcrever áudio:', error);
      setSnackbarMessage('Erro ao transcrever áudio');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const textTypes = [
    { value: 'consulta', label: 'Consulta' },
    { value: 'retorno', label: 'Retorno' },
    { value: 'exame', label: 'Exame' },
    { value: 'laudo', label: 'Laudo' },
    { value: 'atestado', label: 'Atestado' },
    { value: 'receita', label: 'Receita' }
  ];

  const getPromptForType = (type: string): string => {
    const prompts: { [key: string]: string } = {
      consulta: `Formate esta consulta médica mantendo todas as informações importantes.
                 Organize nas seguintes seções:
                 - Queixa Principal
                 - História da Doença Atual
                 - Exame Físico
                 - Hipóteses Diagnósticas
                 - Conduta
                 Use terminologia médica apropriada e mantenha a formatação clara.`,

      retorno: `Formate esta consulta de retorno mantendo todas as informações importantes.
                Organize nas seguintes seções:
                - Evolução desde última consulta
                - Resposta ao tratamento
                - Novos sintomas ou queixas
                - Exame Físico Atual
                - Conduta
                Use terminologia médica apropriada e mantenha a formatação clara.`,

      exame: `Formate este resultado de exame mantendo todas as informações importantes.
              Organize nas seguintes seções:
              - Tipo de Exame
              - Achados Principais
              - Conclusão
              Use terminologia médica apropriada e mantenha a formatação técnica padrão.`,

      laudo: `Formate este laudo médico mantendo todas as informações importantes.
              Organize nas seguintes seções:
              - Identificação
              - História Clínica
              - Exames Complementares
              - Diagnóstico
              - Conclusão/Parecer
              Use terminologia médica apropriada e formato oficial de laudo.`,

      atestado: `Formate este atestado médico mantendo todas as informações importantes.
                 Organize de forma clara com:
                 - Identificação do paciente
                 - Tempo de afastamento
                 - CID (se fornecido)
                 - Data e assinatura
                 Use formato oficial de atestado médico.`,

      receita: `Formate esta receita médica mantendo todas as informações importantes.
                Organize com:
                - Medicamentos e dosagens
                - Posologia
                - Duração do tratamento
                - Orientações especiais
                Use formato padrão de receituário médico.`
    };

    return prompts[type] || prompts.consulta;
  };

  const handleFormatText = async () => {
    if (!transcriptionData.transcriptionText) {
      setSnackbarMessage('Não há texto para formatar');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      setIsFormatting(true);
      console.log('Enviando texto para formatação:', {
        text: transcriptionData.transcriptionText,
        type: transcriptionData.type,
        patientName: transcriptionData.patientName || ''
      });

      const response = await api.post('/format', {
        text: transcriptionData.transcriptionText,
        type: transcriptionData.type || 'consulta',
        patientName: transcriptionData.patientName || ''
      });

      console.log('Resposta da formatação:', response.data);

      if (response.data && response.data.text) {
        setTranscriptionData(prev => ({
          ...prev,
          transcricaoIa: response.data.text
        }));

        setSnackbarMessage('Texto formatado com sucesso!');
        setSnackbarSeverity('success');
      } else {
        throw new Error('Erro ao formatar texto');
      }
    } catch (error) {
      console.error('Erro ao formatar texto:', error);
      setSnackbarMessage('Erro ao formatar texto');
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
      setIsFormatting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsSaving(true);
      console.log('[Salvamento] Iniciando salvamento da transcrição:', {
        hasAudio: !!transcriptionData.audioBlob,
        saveAudioFile,
        textLength: transcriptionData.transcriptionText.length
      });

      if (!transcriptionData.transcriptionText.trim()) {
        setSnackbarMessage('Por favor, insira um texto para transcrição');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      const formData = new FormData();
      formData.append('transcriptionText', transcriptionData.transcriptionText);
      formData.append('transcricaoIa', transcriptionData.transcricaoIa);
      formData.append('patientName', transcriptionData.patientName);
      formData.append('type', transcriptionData.type);
      formData.append('notes', transcriptionData.notes);
      formData.append('status', transcriptionData.status);
      formData.append('isRealTime', String(isRealTime));
      formData.append('saveAudioFile', String(saveAudioFile));

      if (saveAudioFile && transcriptionData.audioBlob) {
        const filename = transcriptionData.audioFilename || 'recording.webm';
        console.log('[Salvamento] Preparando arquivo de áudio:', {
          type: transcriptionData.audioBlob.type,
          size: transcriptionData.audioBlob.size,
          filename
        });
        formData.append('audio', transcriptionData.audioBlob, filename);
      }

      const response = await api.post('/transcriptions', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('[Salvamento] Transcrição salva com sucesso:', response.data);
      
      setSnackbarMessage('Transcrição salva com sucesso!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Limpar o formulário
      setTranscriptionData({
        transcriptionText: '',
        transcricaoIa: '',
        patientName: '',
        type: '',
        notes: '',
        status: 'completed'
      });
      setAudioPreviewUrl(null);
      
    } catch (error: any) {
      console.error('[Salvamento] Erro ao salvar transcrição:', error);
      setSnackbarMessage(`Erro ao salvar: ${error?.response?.data?.error || error?.message || 'Erro desconhecido'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbarMessage('Texto copiado para a área de transferência!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage('Erro ao copiar texto');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <Container maxWidth="lg">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom>
            Nova Transcrição
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Nome do Paciente"
            value={transcriptionData.patientName || ''}
            onChange={(e) => setTranscriptionData(prev => ({ ...prev, patientName: e.target.value }))}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Tipo de Texto</InputLabel>
            <Select
              value={transcriptionData.type || 'consulta'}
              onChange={(e) => setTranscriptionData(prev => ({ ...prev, type: e.target.value }))}
              label="Tipo de Texto"
            >
              {textTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isRealTime}
                      onChange={(e) => setIsRealTime(e.target.checked)}
                      disabled={isRecording}
                    />
                  }
                  label="Transcrição em tempo real"
                />
              </Grid>

              <Grid item>
                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                  {!isRecording ? (
                    <>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<KeyboardVoiceIcon />}
                        onClick={startRecording}
                      >
                        Iniciar Gravação
                      </Button>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={saveAudioFile}
                            onChange={(e) => setSaveAudioFile(e.target.checked)}
                          />
                        }
                        label="Salvar arquivo de áudio"
                      />
                    </>
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        color={isPaused ? "primary" : "secondary"}
                        startIcon={isPaused ? <KeyboardVoiceIcon /> : <StopIcon />}
                        onClick={pauseRecording}
                      >
                        {isPaused ? "Continuar" : "Pausar"}
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<StopIcon />}
                        onClick={stopRecording}
                      >
                        Parar
                      </Button>
                    </>
                  )}
                </Box>
                {recordingTime > 0 && (
                  <Typography variant="body2" color="textSecondary">
                    Tempo de gravação: {formatTime(recordingTime)}
                  </Typography>
                )}
                {audioPreviewUrl && saveAudioFile && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Prévia do Áudio:
                    </Typography>
                    <audio controls src={audioPreviewUrl} style={{ width: '100%' }} />
                  </Box>
                )}
              </Grid>

              {!isRealTime && (
                <Grid item>
                  <input
                    type="file"
                    accept="audio/*"
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isRecording}
                  >
                    Upload
                  </Button>
                </Grid>
              )}

              {audioUrl && !isRealTime && (
                <Grid item xs={12}>
                  <Box mt={2}>
                    <audio controls src={audioUrl} />
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleTranscribeAudio}
                      startIcon={<AIIcon />}
                      sx={{ mt: 1 }}
                    >
                      Transcrever com IA
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={8}
            label="Transcrição Original"
            value={transcriptionData.transcriptionText}
            onChange={(e) => setTranscriptionData(prev => ({
              ...prev,
              transcriptionText: e.target.value
            }))}
            variant="outlined"
            disabled={isRecording && !isPaused}
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleFormatText}
              disabled={isFormatting || !transcriptionData.transcriptionText}
              startIcon={<FormatAlignLeftIcon />}
            >
              {isFormatting ? 'Formatando...' : 'Formatar Texto'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleCopyText(transcriptionData.transcriptionText)}
              startIcon={<ContentCopy />}
              disabled={!transcriptionData.transcriptionText}
            >
              Copiar Texto Original
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={8}
            label="Transcrição IA"
            value={transcriptionData.transcricaoIa}
            onChange={(e) => setTranscriptionData(prev => ({
              ...prev,
              transcricaoIa: e.target.value
            }))}
            variant="outlined"
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="outlined"
              onClick={() => handleCopyText(transcriptionData.transcricaoIa)}
              startIcon={<ContentCopy />}
              disabled={!transcriptionData.transcricaoIa}
            >
              Copiar Texto Formatado
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Observações"
            multiline
            rows={3}
            value={transcriptionData.notes}
            onChange={(e) => setTranscriptionData(prev => ({ ...prev, notes: e.target.value }))}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!transcriptionData.transcriptionText}
            startIcon={<SaveIcon />}
          >
            Salvar
          </Button>
        </Grid>
      </Grid>

      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default NewTranscription;
